import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { useShop } from '../../shop/ShopProvider';

export type PurchaseOrder = Schemas['PurchaseOrder'];
export type PurchaseOrderLine = PurchaseOrder['lines'][number];
export type PurchaseOrderSummary = Schemas['PurchaseOrderList']['data'][number];
export type PurchaseOrderStatus = PurchaseOrder['status'];
export type GoodsReceipt = Schemas['GoodsReceipt'];
export type GoodsReceiptLine = GoodsReceipt['lines'][number];
export type GoodsReceiptSummary = Schemas['GoodsReceiptList']['data'][number];
export type SupplierInvoice = Schemas['SupplierInvoice'];
export type SupplierInvoiceLine = SupplierInvoice['lines'][number];
export type SupplierInvoiceSummary = Schemas['SupplierInvoiceList']['data'][number];
export type CreatePurchaseOrderBody = Schemas['CreatePurchaseOrderRequest'];
export type UpdatePurchaseOrderBody = Schemas['UpdatePurchaseOrderRequest'];
export type CreateGoodsReceiptBody = Schemas['CreateGoodsReceiptRequest'];
export type CreateSupplierInvoiceBody = Schemas['CreateSupplierInvoiceRequest'];
export type TaxRate = Schemas['TaxRate'];
export type Location = Schemas['Location'];
export type Account = Schemas['Account'];

/** Query keys of this feature; mutations invalidate the whole `purchases` tree. */
export const keys = {
  all: ['purchases'] as const,
  orders: (params: object) => ['purchases', 'orders', params] as const,
  order: (id: string) => ['purchases', 'order', id] as const,
  receipts: (params: object) => ['purchases', 'receipts', params] as const,
  receipt: (id: string) => ['purchases', 'receipt', id] as const,
  bills: (params: object) => ['purchases', 'bills', params] as const,
  bill: (id: string) => ['purchases', 'bill', id] as const,
};

export interface ListParams {
  page?: number;
  pageSize?: number;
  q?: string;
  status?: string;
  contactId?: string;
  purchaseOrderId?: string;
  from?: string;
  to?: string;
}

function clean<T extends object>(params: T): T {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== ''),
  ) as T;
}

export function usePurchaseOrders(params: ListParams, enabled = true) {
  return useQuery({
    queryKey: keys.orders(params),
    queryFn: () =>
      unwrap(
        api.GET('/purchase-orders', {
          params: {
            query: clean({
              ...params,
              status: params.status as PurchaseOrderStatus | undefined,
              purchaseOrderId: undefined,
            }),
          },
        }),
      ),
    placeholderData: (prev) => prev,
    enabled,
  });
}

export function usePurchaseOrder(id: string | undefined) {
  return useQuery({
    queryKey: keys.order(id ?? ''),
    queryFn: () => unwrap(api.GET('/purchase-orders/{id}', { params: { path: { id: id ?? '' } } })),
    enabled: !!id,
  });
}

export function useGoodsReceipts(params: ListParams, enabled = true) {
  return useQuery({
    queryKey: keys.receipts(params),
    queryFn: () =>
      unwrap(
        api.GET('/goods-receipts', {
          params: {
            query: clean({ ...params, status: params.status as 'POSTED' | 'REVERSED' | undefined }),
          },
        }),
      ),
    placeholderData: (prev) => prev,
    enabled,
  });
}

export function fetchGoodsReceipt(id: string) {
  return unwrap(api.GET('/goods-receipts/{id}', { params: { path: { id } } }));
}

export function useGoodsReceipt(id: string | undefined) {
  return useQuery({
    queryKey: keys.receipt(id ?? ''),
    queryFn: () => fetchGoodsReceipt(id ?? ''),
    enabled: !!id,
  });
}

export function useSupplierInvoices(params: ListParams, enabled = true) {
  return useQuery({
    queryKey: keys.bills(params),
    queryFn: () =>
      unwrap(
        api.GET('/supplier-invoices', {
          params: {
            query: clean({
              ...params,
              status: params.status as 'DRAFT' | 'POSTED' | 'CANCELLED' | undefined,
            }),
          },
        }),
      ),
    placeholderData: (prev) => prev,
    enabled,
  });
}

export function useSupplierInvoice(id: string | undefined) {
  return useQuery({
    queryKey: keys.bill(id ?? ''),
    queryFn: () =>
      unwrap(api.GET('/supplier-invoices/{id}', { params: { path: { id: id ?? '' } } })),
    enabled: !!id,
  });
}

// --- reference data (each is optional: missing permissions just hide the related field) ------

export function useTaxRates() {
  const { can } = useAuth();
  const { settings } = useShop();
  const enabled = can('settings.view') && settings?.taxEnabled !== false;
  return useQuery({
    queryKey: ['tax-rates'],
    queryFn: () => unwrap(api.GET('/tax-rates')),
    enabled,
    staleTime: 5 * 60_000,
    select: (d) => d.data,
  });
}

export function useLocations() {
  const { can } = useAuth();
  return useQuery({
    queryKey: ['locations'],
    queryFn: () => unwrap(api.GET('/locations')),
    enabled: can('inventory.view'),
    staleTime: 5 * 60_000,
    select: (d) => d.data.filter((l) => l.isActive),
  });
}

export function useExpenseAccounts(enabled = true) {
  const { me } = useAuth();
  const perms = new Set(me?.permissions ?? []);
  const allowed = ['accounts.manage', 'journal.view', 'ledger.view'].some((p) => perms.has(p));
  return useQuery({
    queryKey: ['accounts'],
    queryFn: () => unwrap(api.GET('/accounts')),
    enabled: enabled && allowed,
    staleTime: 5 * 60_000,
    select: (d) => d.data.filter((a) => a.type === 'EXPENSE' && a.isActive && !a.systemKey),
  });
}

export function useProduct(productId: string | undefined) {
  return useQuery({
    queryKey: ['products', 'detail', productId ?? ''],
    queryFn: () => unwrap(api.GET('/products/{id}', { params: { path: { id: productId ?? '' } } })),
    enabled: !!productId,
    staleTime: 5 * 60_000,
  });
}

/** A mutation that invalidates every purchases query (and contacts' balances) on success. */
export function usePurchasesMutation<TVars, TData>(
  fn: (vars: TVars) => Promise<TData>,
  extraKeys: QueryKey[] = [],
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: keys.all }),
        qc.invalidateQueries({ queryKey: ['contacts'] }),
        qc.invalidateQueries({ queryKey: ['inventory'] }),
        qc.invalidateQueries({ queryKey: ['reports'] }),
        ...extraKeys.map((k) => qc.invalidateQueries({ queryKey: k })),
      ]);
    },
  });
}

/** A fresh Idempotency-Key per call (the client would add one anyway; the generated types want it). */
function idem() {
  return { 'idempotency-key': crypto.randomUUID() };
}

export const purchaseApi = {
  getOrder: (id: string) => unwrap(api.GET('/purchase-orders/{id}', { params: { path: { id } } })),
  createOrder: (body: CreatePurchaseOrderBody) =>
    unwrap(api.POST('/purchase-orders', { params: { header: idem() }, body })),
  updateOrder: (id: string, body: UpdatePurchaseOrderBody) =>
    unwrap(api.PATCH('/purchase-orders/{id}', { params: { path: { id } }, body })),
  confirmOrder: (id: string, version: number) =>
    unwrap(
      api.POST('/purchase-orders/{id}/confirm', {
        params: { path: { id }, header: idem() },
        body: { version },
      }),
    ),
  cancelOrder: (id: string, version: number, reason: string) =>
    unwrap(
      api.POST('/purchase-orders/{id}/cancel', {
        params: { path: { id }, header: idem() },
        body: { version, reason },
      }),
    ),
  closeOrder: (id: string, version: number, reason?: string) =>
    unwrap(
      api.POST('/purchase-orders/{id}/close', {
        params: { path: { id }, header: idem() },
        body: { version, ...(reason ? { reason } : {}) },
      }),
    ),
  receive: (body: CreateGoodsReceiptBody) =>
    unwrap(api.POST('/goods-receipts', { params: { header: idem() }, body })),
  reverseReceipt: (id: string, version: number, reason: string) =>
    unwrap(
      api.POST('/goods-receipts/{id}/reverse', {
        params: { path: { id }, header: idem() },
        body: { version, reason },
      }),
    ),
  createBill: (body: CreateSupplierInvoiceBody) =>
    unwrap(api.POST('/supplier-invoices', { params: { header: idem() }, body })),
  cancelBill: (id: string, version: number, reason: string) =>
    unwrap(
      api.POST('/supplier-invoices/{id}/cancel', {
        params: { path: { id }, header: idem() },
        body: { version, reason },
      }),
    ),
};
